// LeaveApprovalDashboard — real pending-approval queue (no mock), KaziPeople
// design system. Manager approves/denies their team's leave requests.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/lib/apiError';
import { useConfirmIfAvailable } from '@/features/shared/components/confirm/ConfirmProvider';
import { CoverageStatusBadge, confirmApproveAnyway } from '@/features/delegation/CoverageStatusBadge';
import { AssignCoverAction } from '@/features/delegation/AssignCoverAction';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/features/shared/components/ui/avatar';
import { Check, X, Clock, CalendarDays, Loader2, Building2 } from 'lucide-react';
import {
  getPendingApprovals,
  approveLeaveRequest,
  rejectLeaveRequest,
  getEmployees,
  getDepartments,
} from '@/lib/hrApi';
import { invalidateDelegatedCapabilities } from '@/features/delegation/useDelegatedCapabilities';
import { fmtRange } from './leaveDateRange';

interface Req {
  id: string;
  name: string;
  /** The requester's auth user id (never offered as their own cover). */
  userId?: string;
  avatar?: string | null;
  department?: string;
  type: string;
  start: string;
  end: string;
  days: number;
  reason: string;
  submitted: string;
  coverageStatus?: string | null;
  coverageReason?: string | null;
  coverageDelegateName?: string | null;
}

const initials = (n: string) => n.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
const fmtWhen = (s?: string) => (s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

export const LeaveApprovalDashboard: React.FC = () => {
  const { t } = useTranslation('manager');
  const confirm = useConfirmIfAvailable();
  const [requests, setRequests] = useState<Req[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [denyTarget, setDenyTarget] = useState<Req | null>(null);
  const [denyReason, setDenyReason] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [pending, emps, depts] = await Promise.all([
          getPendingApprovals().catch((e: any) => {
            // A delegate whose delegation was revoked or expired: re-ask auth,
            // so the page and its nav item go without a reload (web #62).
            if (e?.response?.status === 403) invalidateDelegatedCapabilities();
            return [] as any[];
          }),
          getEmployees({ page: 1, page_size: 100 }).catch(() => ({ items: [] as any[] })),
          getDepartments().catch(() => ({ items: [] as any[] })),
        ]);
        const byUser = new Map<string, any>((emps.items || []).map((e: any) => [String(e.user_id), e]));
        const deptName = new Map<string, string>((depts.items || []).map((d: any) => [d.id, d.name]));
        const rows: Req[] = (Array.isArray(pending) ? pending : []).map((r: any) => {
          const uid = String(r.employee_id ?? r.user_id ?? '');
          const e = byUser.get(uid);
          const name = e ? `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim() || e.email : r.employee_name || 'Teammate';
          return {
            id: String(r.id),
            userId: uid || undefined,
            name,
            avatar: e?.profile_picture_url,
            department: (e?.department_id && deptName.get(e.department_id)) || undefined,
            type: r.leave_type?.name || r.type_name || r.type || r.leave_type_code || 'Leave',
            start: String(r.start_date || '').slice(0, 10),
            end: String(r.end_date || r.start_date || '').slice(0, 10),
            days: Number(r.days_requested ?? r.days ?? 0),
            reason: r.reason || '—',
            submitted: r.submitted_at || r.created_at || '',
            coverageStatus: r.coverage_status ?? null,
            coverageReason: r.coverage_reason ?? null,
            coverageDelegateName: r.coverage_delegate_name ?? null,
          };
        });
        if (active) setRequests(rows);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const approve = async (r: Req) => {
    // Cover not confirmed → ask, never block; approving anyway is recorded.
    if (!(await confirmApproveAnyway(confirm, t as any, { status: r.coverageStatus, reason: r.coverageReason, delegateName: r.coverageDelegateName }))) return;
    setBusy(r.id);
    try {
      await approveLeaveRequest(r.id);
      setRequests((prev) => prev.filter((x) => x.id !== r.id));
      toast.success(t('leave_approvals.approved_toast', { defaultValue: 'Approved {{name}}’s {{type}}', name: r.name, type: r.type.toLowerCase() }));
    } catch (e: any) {
      if (e?.response?.status === 403) invalidateDelegatedCapabilities();
      // Show the backend's own reason when it refuses (e.g. self-approval).
      toast.error(getApiErrorMessage(e, t('leave_approvals.approve_failed', { defaultValue: 'Could not approve request' })), { duration: 6000 });
    } finally {
      setBusy(null);
    }
  };
  const confirmDeny = async () => {
    if (!denyTarget) return;
    const reason = denyReason.trim();
    if (!reason) {
      toast.error(t('leave_approvals.deny_reason_required', { defaultValue: 'Please provide a reason for rejection' }));
      return;
    }
    setBusy(denyTarget.id);
    try {
      await rejectLeaveRequest(denyTarget.id, reason);
      setRequests((prev) => prev.filter((x) => x.id !== denyTarget.id));
      toast.success(t('leave_approvals.denied', { defaultValue: 'Request denied' }));
      setDenyTarget(null);
    } catch (e: any) {
      if (e?.response?.status === 403) invalidateDelegatedCapabilities();
      toast.error(getApiErrorMessage(e, t('leave_approvals.deny_failed', { defaultValue: 'Could not deny request' })), { duration: 6000 });
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
    <Card className="border-orange-100 dark:border-orange-500/20">
      <CardHeader className="bg-gradient-to-r from-orange-50 dark:from-orange-500/[0.08] to-amber-50 dark:to-amber-500/[0.05] rounded-t-xl">
        <CardTitle className="flex items-center gap-2 text-orange-700">
          <Clock className="w-5 h-5" /> {t('leave_approvals.queue', { defaultValue: 'Pending requests' })} ({requests.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        {loading ? (
          <div className="py-12 flex items-center justify-center text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('common.loading', { defaultValue: 'Loading…' })}
          </div>
        ) : requests.length === 0 ? (
          <div className="py-16 flex flex-col items-center text-center">
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-[16px] bg-green-100 dark:bg-green-500/[0.14] text-green-600 dark:text-green-300 mb-4">
              <Check className="w-7 h-7" />
            </span>
            <h3 className="text-h4 text-foreground">{t('leave_approvals.empty_title', { defaultValue: 'All caught up' })}</h3>
            <p className="text-muted-foreground mt-1">{t('leave_approvals.empty', { defaultValue: 'No leave requests waiting for your approval.' })}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((r) => (
              <div key={r.id} className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-start gap-3 flex-wrap">
                  <Avatar className="w-11 h-11 border-2 border-orange-200 dark:border-orange-500/25">
                    {r.avatar ? <AvatarImage src={r.avatar} alt={r.name} /> : null}
                    <AvatarFallback className="bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700">{initials(r.name)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-[180px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-foreground">{r.name}</span>
                      {r.department && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <Building2 className="w-3.5 h-3.5" /> {r.department}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1 flex-wrap">
                      <CalendarDays className="w-4 h-4 text-orange-500" />
                      <span className="text-foreground font-medium">{r.type}</span>
                      <span>·</span>
                      <span>{fmtRange(r.start, r.end)}</span>
                      <span>·</span>
                      <span>{r.days} {r.days === 1 ? t('leave_approvals.day', { defaultValue: 'day' }) : t('leave_approvals.days', { defaultValue: 'days' })}</span>
                    </div>
                    {r.reason && r.reason !== '—' && (
                      <p className="text-sm text-muted-foreground mt-1">“{r.reason}”</p>
                    )}
                    <CoverageStatusBadge className="mt-1.5" status={r.coverageStatus} reason={r.coverageReason} delegateName={r.coverageDelegateName} withReason />
                    {/* No cover / cover needs attention → the approver can arrange it. */}
                    <AssignCoverAction
                      requestId={r.id}
                      employeeName={r.name}
                      employeeUserId={r.userId}
                      dates={fmtRange(r.start, r.end)}
                      coverageStatus={r.coverageStatus}
                      coverageReason={r.coverageReason}
                      coverageDelegateName={r.coverageDelegateName}
                      onAssigned={(c) =>
                        setRequests((prev) =>
                          prev.map((x) =>
                            x.id === r.id
                              ? { ...x, coverageStatus: c.coverage_status, coverageReason: c.coverage_reason, coverageDelegateName: c.coverage_delegate_name }
                              : x,
                          ),
                        )
                      }
                    />
                    <p className="text-xs text-muted-foreground mt-1">{t('leave_approvals.submitted', { defaultValue: 'Submitted' })} {fmtWhen(r.submitted)}</p>
                  </div>
                  <div className="flex gap-2 ml-auto">
                    <button
                      type="button"
                      onClick={() => approve(r)}
                      disabled={busy === r.id}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[10px] bg-green-600 hover:bg-green-700 text-white text-sm font-medium disabled:opacity-60 transition-colors"
                    >
                      {busy === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      {t('leave_approvals.approve', { defaultValue: 'Approve' })}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setDenyTarget(r); setDenyReason(''); }}
                      disabled={busy === r.id}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[10px] border border-red-200 text-red-600 hover:bg-red-50 dark:border-red-500/30 dark:hover:bg-red-500/10 text-sm font-medium disabled:opacity-60 transition-colors"
                    >
                      <X className="w-4 h-4" />
                      {t('leave_approvals.deny', { defaultValue: 'Deny' })}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>

    {denyTarget && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setDenyTarget(null)}>
        <div className="bg-card rounded-2xl shadow-xl w-full max-w-md p-6 border border-border" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start gap-3">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-red-100 dark:bg-red-500/[0.14] text-red-600 shrink-0">
              <X className="w-5 h-5" />
            </span>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-foreground">
                {t('leave_approvals.deny_title', { defaultValue: 'Deny this request?' })}
              </h3>
              <p className="text-sm text-muted-foreground mt-1 truncate">
                {denyTarget.name} · {denyTarget.type} · {fmtRange(denyTarget.start, denyTarget.end)}
              </p>
            </div>
          </div>
          <label className="block text-sm font-medium text-foreground mt-5 mb-1" htmlFor="deny-reason">
            {t('leave_approvals.reason', { defaultValue: 'Reason' })}
          </label>
          <textarea
            id="deny-reason"
            autoFocus
            value={denyReason}
            onChange={(e) => setDenyReason(e.target.value)}
            rows={3}
            placeholder={t('leave_approvals.deny_placeholder', { defaultValue: 'Let them know why…' })}
            className="w-full rounded-[10px] border border-border bg-cream px-3 py-2 text-sm text-foreground outline-none focus:border-orange-400 resize-none"
          />
          <div className="flex justify-end gap-2 mt-5">
            <button
              type="button"
              onClick={() => setDenyTarget(null)}
              disabled={busy === denyTarget.id}
              className="px-4 py-2 rounded-[10px] border border-border text-foreground hover:bg-muted text-sm font-medium disabled:opacity-60"
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </button>
            <button
              type="button"
              onClick={confirmDeny}
              disabled={busy === denyTarget.id}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[10px] bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-60"
            >
              {busy === denyTarget.id ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {t('leave_approvals.deny_request', { defaultValue: 'Deny request' })}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default LeaveApprovalDashboard;
